import MarkdownIt from 'markdown-it';
import classPlugin from 'markdown-it-class';
import { full as emojiPlugin } from 'markdown-it-emoji';
import { alertPlugin } from 'markdown-it-github-alert';
import highlightPlugin from 'markdown-it-highlight';
import linkAttributesPlugin from 'markdown-it-link-attributes';
import taskListPlugin from 'markdown-it-task-lists';
// this following line is only required for the UI gem
import 'markdown-it-highlight/dist/index.css';
import DOMPurify from 'dompurify';

// Our policy for rendering is making the README on FEX as close to GitHub. when there is a conflict on rendering between MathWorks web standards and GitHub, we always pick GitHub

export class MarkdownRenderer {
    constructor () {
        this.md = MarkdownIt({
            html: true,
            linkify: true,
            typographer: true
        });
        this.initializePlugins();
    }

    initializePlugins () {
        this.md.use(emojiPlugin)
            .use(alertPlugin)
            .use(highlightPlugin)
            .use(classPlugin, {
                table: ['readme_table'],
                th: ['readme_th'],
                td: ['readme_td'],
                h1: ['readme_h1']
            }).use(linkAttributesPlugin, {
                attrs: {
                    target: '_blank',
                    rel: 'nofollow noopener noreferrer'
                }
            }).use(taskListPlugin);
    }

    normalizeWhitespace(content) {
        // Strip UTF-8 BOM that some editors prepend to files
        return content.replace(/^\uFEFF/, '');
    }

    // For github links that lead to .md files we want to open the 'blob' instead of 'raw' versions so that they are displayed as rendered markdown files.
    normalizeGitHubURLPath (githubRepoUrl, branchOrCommitSubpath) {
        if (branchOrCommitSubpath.endsWith('.md')) {
            return `${githubRepoUrl}/blob/${branchOrCommitSubpath}`;
        }

        return `${githubRepoUrl}/raw/${branchOrCommitSubpath}`;
    }

    // Function to normalize GitHub blob URLs to raw content URLs.
    // We need this because HTML doesn't recognize the URL as valid sources when the URL is like below:
    // 'https://github.com/my-github/my-repo/blob/my-branch/myfile'
    // Thus, we need to convert them to:
    // 'https://github.com/my-github/my-repo/raw/my-branch/myfile'
    normalizeGitHubURLs (mdHTML) {
        return mdHTML.replace(/https:\/\/github\.com\/([^\/]+\/[^\/]+)\/blob\/([^\/]+\/[^"'\s]+)/g, (_, repo, path) => {
            return this.normalizeGitHubURLPath(`https://github.com/${repo}`, path);
        });
    }

    // Function to convert relative URLs to absolute URLs based on GitHub repo URL and branch or commit
    // Similar to the above, we need this because HTML doesn't recognize the relative URL as valid sources when the URL is like below:
    // '/my-branch/myfile'
    // Thus, we need to convert them to:
    // 'https://github.com/my-github/my-repo/raw/my-branch/myfile'
    convertRelativeURLsToAbsolute (mdHTML, branchOrCommit, githubRepoUrl) {
        return mdHTML.replace(/(href|src|action|srcset)="([^":]+?)"/g, (match, attributeName, relativeUrl) => {
            if (!/^[a-zA-Z]+:\/\//.test(relativeUrl)) {
                const prefixedUrl = this.normalizeGitHubURLPath(githubRepoUrl, `${branchOrCommit}/${relativeUrl}`);
                return `${attributeName}="${prefixedUrl}"`;
            } else {
                return match;
            }
        });
    }

    rewriteHTML (mdHTML, branchOrCommit, githubRepoUrl) {
        mdHTML = this.normalizeGitHubURLs(mdHTML);
        if (branchOrCommit && githubRepoUrl) {
            mdHTML = this.convertRelativeURLsToAbsolute(mdHTML, branchOrCommit, githubRepoUrl);
        }
        return mdHTML;
    }

    // convert height=SOME_HEIGHT to style="height: SOMEHEIGHT". This fix was first imported in this
    // commit: https://github.mathworks.com/development/addons-contribution-ui/blob/b55bfd6977467f9be6a0e87ea4f16fcbeae26b3a/lib/github/readme_html_rewriter.rb
    addInlineImageStyles (imagesWithHeight) {
        imagesWithHeight.forEach((img) => {
            const heightValue = img.getAttribute('height');
            if (heightValue.endsWith('px') || heightValue.endsWith('%')) {
                img.style.height = heightValue;
            } else {
                img.style.height = `${heightValue}px`;
            }
            img.removeAttribute('height');
        });
    }

    embedYouTubeVideos (container) {
        const WATCH_URL_PATTERN = 'youtube\\.com\\/watch\\?v=';
        const SHORT_URL_PATTERN = 'youtu\\.be\\/';
        const VIDEO_ID_PATTERN = '[\\w.%-]+';
        const YOUTUBE_REGEX = new RegExp(
            `https?:\\/\\/(?:www\\.)?(?:${WATCH_URL_PATTERN}|${SHORT_URL_PATTERN})(${VIDEO_ID_PATTERN})`, 'g'
        );
        const embeddedIds = new Set();

        const processNode = (node) => {
            if (node.tagName === 'A') {
                const href = node.getAttribute('href') || '';
                const match = YOUTUBE_REGEX.exec(href);
                YOUTUBE_REGEX.lastIndex = 0;
                if (match && !embeddedIds.has(match[1])) {
                    embeddedIds.add(match[1]);
                    const block = this.closestBlockElement(node);
                    block.insertAdjacentHTML('afterend', this.youtubeIframe(match[1]));
                }
            } else if (node.children && node.children.length > 0) {
                Array.from(node.children).forEach(child => processNode(child));
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
                let match;
                while ((match = YOUTUBE_REGEX.exec(node.textContent)) !== null) {
                    if (!embeddedIds.has(match[1])) {
                        embeddedIds.add(match[1]);
                        const block = this.closestBlockElement(node.parentElement);
                        block.insertAdjacentHTML('afterend', this.youtubeIframe(match[1]));
                    }
                }
            }
        };

        processNode(container);
    }

    closestBlockElement (node) {
        const blockElements = new Set(['DIV', 'P', 'BLOCKQUOTE', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'PRE', 'TABLE', 'SECTION', 'ARTICLE']);
        let current = node;
        while (current.parentElement) {
            if (blockElements.has(current.tagName)) {
                return current;
            }
            current = current.parentElement;
        }
        return current;
    }

    youtubeIframe (videoId) {
        return `<iframe src="https://www.youtube.com/embed/${videoId}" allow="fullscreen" frameborder="0" width="640" height="360"></iframe>`;
    }

    renderToElement (element, markdownContent, { branchOrCommit = '', githubRepoUrl = '' } = {}) {
        markdownContent = this.normalizeWhitespace(markdownContent);
        const mdHTML = this.md.render(markdownContent);
        let resultHTML = this.rewriteHTML(mdHTML, branchOrCommit, githubRepoUrl);
        if (!resultHTML) {
            resultHTML = markdownContent;
        }

        let sanitizedHTML = '';
        try {
            sanitizedHTML = DOMPurify.sanitize(resultHTML, { ADD_ATTR: ['target', 'allow', 'frameborder'] });
        } catch (domPurifyError) {
            console.error("Error sanitizing HTML:", domPurifyError);
        }

        element.innerHTML = sanitizedHTML;

        this.embedYouTubeVideos(element);
        this.addInlineImageStyles(element.querySelectorAll('img[height]'));

        if (typeof MathJax !== 'undefined') {
            // we should sanitize with DOMPurify after MathJax because of potential XSS attacks,
            // however it's causing display issues for all versions of AOE in MATLAB, so we are okay skipping this DOMPurify
            // after consulting with the security team
            MathJax.typesetPromise([element]).catch(mathJaxError => {
                console.error("Error rendering MathJax:", mathJaxError);
            });
        }
    }

    render (markdownDescriptionElement) {
        const branchOrCommit = markdownDescriptionElement.dataset.branchOrCommit;
        const githubRepoUrl = markdownDescriptionElement.dataset.repositoryUrl;
        const markdownContent = markdownDescriptionElement.dataset.readmeMarkdown;

        this.renderToElement(markdownDescriptionElement, markdownContent, {
            branchOrCommit,
            githubRepoUrl
        });
    }
}
